import { useState } from 'react';
import { Box, Button, Typography, Paper, CircularProgress, Alert, LinearProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Upload } from 'lucide-react';
import { useCurrentUser } from '@diggens/user-management';
import { uploadFile, apiFetch } from '@diggens/api-client';
import { NODE_REGISTRY } from '@diggens/types/graph/nodes';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

interface CrmImportFormProps {
  file: File;
  onSuccess?: (result: { inserted: number; skipped: number; message: string }) => void;
  onCancel?: () => void;
}

const IMPORTABLE_NODE_TYPES = Object.entries(NODE_REGISTRY)
  .filter(([_, config]) => config.naturalKeyField)
  .map(([type, config]) => ({
    type,
    label: config.displayNamePlural,
    description: config.description,
  }));

export function CrmImportForm({ file, onSuccess, onCancel }: CrmImportFormProps) {
  const { data: user } = useCurrentUser();
  const [nodeType, setNodeType] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');

  const handleImport = async () => {
    if (!nodeType || !user?.tenant_id) {
      setError('Please select a node type');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      setStatus('Uploading file...');
      const timestamp = Date.now();
      const filePath = `${user.tenant_id}/${timestamp}-${file.name}`;

      const { key } = await uploadFile('crm-imports', filePath, file);

      setStatus('Processing import...');
      const result = await apiFetch<any>('/crm/import', {
        method: 'POST',
        body: JSON.stringify({
          file_path: key,
          node_type: nodeType,
        }),
      });

      if (!result.success) {
        const errorMessage = result.error || 'Import failed';
        const suggestions = result.suggestions || [];
        const detailedError = suggestions.length > 0 ? `${errorMessage}\n\nSuggestions:\n${suggestions.map((s: string) => `• ${s}`).join('\n')}\n\n${result.help || ''}` : errorMessage;
        throw new Error(detailedError);
      }

      setStatus('Complete!');

      const message = result.message || `Imported ${result.inserted} ${nodeType}(s)`;
      setTimeout(() => {
        onSuccess?.({
          inserted: result.inserted || 0,
          skipped: result.skipped || 0,
          message,
        });
      }, 1500);
    } catch (err: any) {
      const detail = err.detail ? `\n\n${err.detail}` : '';
      setError((err.message || 'Failed to import data') + detail);
      setIsProcessing(false);
    }
  };

  return (
    <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, mt: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Import CSV Data
      </Typography>

      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Upload size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {file.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({(file.size / 1024).toFixed(1)} KB)
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {error}
        </Alert>
      )}

      {isProcessing && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {status}
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {!isProcessing && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Node Type</InputLabel>
          <Select value={nodeType} onChange={(e) => setNodeType(e.target.value)} label="Node Type" size="small">
            {IMPORTABLE_NODE_TYPES.map((option) => (
              <MenuItem key={option.type} value={option.type}>
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" size="small" onClick={handleImport} disabled={!nodeType || isProcessing}>
          {isProcessing ? <CircularProgress size={16} /> : 'Import'}
        </Button>
        {onCancel && (
          <Button variant="outlined" size="small" onClick={onCancel} disabled={isProcessing}>
            Cancel
          </Button>
        )}
      </Box>
    </Paper>
  );
}
